/**
 * Generador de informe clínico odontológico en PDF.
 *
 * Usa jsPDF + jspdf-autotable para construir el documento enteramente desde
 * datos (odontogramJson + campos clínicos). Sin captura de DOM, sin SVG,
 * sin dependencia de render/timing del navegador.
 *
 * Soporta dos formatos de odontogramJson:
 *   "simple-v1"  — formato propio del nuevo editor de grilla simplificada
 *   otros        — formato ZoliQua v1.3 (legacy); muestra aviso en el PDF
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { hasQuestionnaireContent, type MedicalQuestionnaire, type YesNo } from "./medicalQuestionnaire";

// ── Colores (alineados con cf / sfturnos-styles.ts) ──────────────────────────
const C = {
  ink: [15, 23, 42] as [number, number, number],
  muted: [100, 116, 139] as [number, number, number],
  subtle: [148, 163, 184] as [number, number, number],
  border: [226, 232, 240] as [number, number, number],
  rowAlt: [248, 250, 252] as [number, number, number],
  brand: [99, 102, 241] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
};

// ── Nomenclatura FDI → nombre en español ─────────────────────────────────────

const FDI_NAMES: Record<number, string> = {
  11: "Incisivo central sup. derecho",
  12: "Incisivo lateral sup. derecho",
  13: "Canino superior derecho",
  14: "1er premolar sup. derecho",
  15: "2do premolar sup. derecho",
  16: "1er molar superior derecho",
  17: "2do molar superior derecho",
  18: "3er molar superior derecho",
  21: "Incisivo central sup. izquierdo",
  22: "Incisivo lateral sup. izquierdo",
  23: "Canino superior izquierdo",
  24: "1er premolar sup. izquierdo",
  25: "2do premolar sup. izquierdo",
  26: "1er molar superior izquierdo",
  27: "2do molar superior izquierdo",
  28: "3er molar superior izquierdo",
  31: "Incisivo central inf. izquierdo",
  32: "Incisivo lateral inf. izquierdo",
  33: "Canino inferior izquierdo",
  34: "1er premolar inf. izquierdo",
  35: "2do premolar inf. izquierdo",
  36: "1er molar inferior izquierdo",
  37: "2do molar inferior izquierdo",
  38: "3er molar inferior izquierdo",
  41: "Incisivo central inf. derecho",
  42: "Incisivo lateral inf. derecho",
  43: "Canino inferior derecho",
  44: "1er premolar inf. derecho",
  45: "2do premolar inf. derecho",
  46: "1er molar inferior derecho",
  47: "2do molar inferior derecho",
  48: "3er molar inferior derecho",
  51: "Incisivo central sup. der. (temp.)",
  52: "Incisivo lateral sup. der. (temp.)",
  53: "Canino sup. der. (temp.)",
  54: "1er molar sup. der. (temp.)",
  55: "2do molar sup. der. (temp.)",
  61: "Incisivo central sup. izq. (temp.)",
  62: "Incisivo lateral sup. izq. (temp.)",
  63: "Canino sup. izq. (temp.)",
  64: "1er molar sup. izq. (temp.)",
  65: "2do molar sup. izq. (temp.)",
  71: "Incisivo central inf. izq. (temp.)",
  72: "Incisivo lateral inf. izq. (temp.)",
  73: "Canino inf. izq. (temp.)",
  74: "1er molar inf. izq. (temp.)",
  75: "2do molar inf. izq. (temp.)",
  81: "Incisivo central inf. der. (temp.)",
  82: "Incisivo lateral inf. der. (temp.)",
  83: "Canino inf. der. (temp.)",
  84: "1er molar inf. der. (temp.)",
  85: "2do molar inf. der. (temp.)",
};

// ── Etiquetas del nuevo formato simple-v1 ─────────────────────────────────────

const SURFACE_LABELS: Record<string, string> = {
  vestibular: "vestibular",
  lingual: "lingual/palatino",
  mesial: "mesial",
  distal: "distal",
  oclusal: "oclusal",
};

const SIMPLE_CONDITION_LABELS: Record<string, string> = {
  caries: "Caries",
  obturacion: "Obturación",
  sellante: "Sellante",
  fractura: "Fractura",
  ausente: "Pieza ausente",
  extraccion_indicada: "Extracción indicada",
  corona: "Corona",
  endodoncia: "Endodoncia",
};

// Teeth in the order they appear in the grid (upper arch left→right, lower arch left→right)
const ALL_TEETH_FDI = [
  18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28,
  48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
  55, 54, 53, 52, 51, 61, 62, 63, 64, 65,
  85, 84, 83, 82, 81, 71, 72, 73, 74, 75,
];

// ── Parseo formato simple-v1 ──────────────────────────────────────────────────

type SimpleToothRow = {
  toothNo: number;
  name: string;
  condition: string;
  surfaces: string;
  note: string;
};

function parseSimpleV1(json: unknown): SimpleToothRow[] {
  if (!json || typeof json !== "object") return [];
  const data = json as {
    version?: string;
    teeth?: Record<string, {
      toothCondition?: string | null;
      surfaces?: Record<string, string | null>;
      note?: string;
    }>;
  };
  if (!data.teeth) return [];

  const rows: SimpleToothRow[] = [];

  for (const toothNo of ALL_TEETH_FDI) {
    const key = String(toothNo);
    const t = data.teeth[key];
    if (!t) continue;

    const conditionParts: string[] = [];
    const surfaceParts: string[] = [];

    if (t.toothCondition) {
      conditionParts.push(SIMPLE_CONDITION_LABELS[t.toothCondition] ?? t.toothCondition);
    }

    if (t.surfaces) {
      for (const [surf, cond] of Object.entries(t.surfaces)) {
        if (cond) {
          conditionParts.push(
            `${SIMPLE_CONDITION_LABELS[cond] ?? cond} (${SURFACE_LABELS[surf] ?? surf})`,
          );
          surfaceParts.push(SURFACE_LABELS[surf] ?? surf);
        }
      }
    }

    if (conditionParts.length === 0 && !t.note?.trim()) continue;

    rows.push({
      toothNo,
      name: FDI_NAMES[toothNo] ?? `Pieza ${toothNo}`,
      condition: conditionParts.join("\n") || "Sin registro",
      surfaces: [...new Set(surfaceParts)].join(", ") || "-",
      note: t.note?.trim() ?? "",
    });
  }

  return rows;
}

// ── Payload del generador ─────────────────────────────────────────────────────

export type PdfPayload = {
  clientName: string;
  clientEmail: string;
  clientPhone: string | null;
  clientAddress: string | null;
  clientBirthDate: string | null;
  professionalName: string;
  date: string;
  chiefComplaint: string | null;
  sessionNotes: string | null;
  treatmentsIndicated: string | null;
  treatmentsDone: string | null;
  /** Antecedentes en formato anterior (4 campos libres) — historias creadas antes del cuestionario. */
  medicalHistory: string | null;
  allergies: string | null;
  medications: string | null;
  generalNotes: string | null;
  /** Cuestionario estructurado (formato nuevo). Si tiene contenido, tiene prioridad sobre el legacy. */
  medicalQuestionnaire: MedicalQuestionnaire | null;
  odontogramJson: unknown;
};

function yesNoLabel(v: YesNo): string {
  return v === "yes" ? "Sí" : v === "no" ? "No" : "Sin responder";
}

// ── Layout helpers ────────────────────────────────────────────────────────────

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FOOTER_H = 12;

function drawFooter(doc: jsPDF) {
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const y = PAGE_H - 9;
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y - 3, PAGE_W - MARGIN, y - 3);
    doc.setFontSize(7.5);
    doc.setTextColor(...C.subtle);
    doc.text("SFturnos · sfturnos.com", MARGIN, y, { baseline: "middle" });
    doc.text(`Pagina ${i} de ${totalPages}`, PAGE_W - MARGIN, y, {
      align: "right",
      baseline: "middle",
    });
  }
}

function gap(y: number, mm: number) {
  return y + mm;
}

function fieldBlock(
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  maxW: number,
): number {
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.subtle);
  doc.text(label.toUpperCase(), x, y);
  y += 4;

  doc.setFontSize(9.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.ink);
  const lines = doc.splitTextToSize(value, maxW) as string[];
  doc.text(lines, x, y);
  return y + lines.length * 4.5 + 3;
}

// ── Función principal ─────────────────────────────────────────────────────────

export function generateOdontogramPdf(payload: PdfPayload): void {
  const {
    clientName, clientEmail, clientPhone, clientAddress, clientBirthDate, professionalName,
    date, chiefComplaint, sessionNotes, treatmentsIndicated, treatmentsDone,
    medicalHistory, allergies, medications, generalNotes, medicalQuestionnaire, odontogramJson,
  } = payload;

  let birthDateLabel: string | null = null;
  if (clientBirthDate) {
    try { birthDateLabel = format(new Date(clientBirthDate), "d 'de' MMMM yyyy", { locale: es }); } catch { /* */ }
  }

  let dateLabel = date;
  try { dateLabel = format(new Date(date), "d 'de' MMMM yyyy", { locale: es }); } catch { /* */ }
  const nowLabel = format(new Date(), "d 'de' MMMM yyyy 'a las' HH:mm", { locale: es });

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  let y = MARGIN;

  // ── 1. ENCABEZADO ──────────────────────────────────────────────────────────

  doc.setFillColor(...C.ink);
  doc.rect(MARGIN, y, CONTENT_W, 14, "F");
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.white);
  doc.text("Historia Clinica Odontologica", MARGIN + 4, y + 9);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(200, 205, 215);
  doc.text("Registro clinico interno", PAGE_W - MARGIN - 4, y + 5.5, { align: "right" });
  doc.text("No reemplaza documentacion oficial", PAGE_W - MARGIN - 4, y + 10, { align: "right" });

  y = gap(y, 18);

  // ── 2. META: PACIENTE / PROFESIONAL / FECHA ────────────────────────────────

  const colW = CONTENT_W / 3;

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.subtle);
  doc.text("PACIENTE", MARGIN, y);
  doc.setFontSize(10.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.ink);
  doc.text(clientName, MARGIN, y + 4.5);
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.muted);
  let subY = y + 9;
  if (clientEmail) { doc.text(clientEmail, MARGIN, subY); subY += 4; }
  if (clientPhone) { doc.text(clientPhone, MARGIN, subY); subY += 4; }
  if (clientAddress) { doc.text(clientAddress, MARGIN, subY); subY += 4; }
  if (birthDateLabel) { doc.text(`Nac.: ${birthDateLabel}`, MARGIN, subY); }

  const col2 = MARGIN + colW;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.subtle);
  doc.text("PROFESIONAL", col2, y);
  doc.setFontSize(10.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.ink);
  doc.text(professionalName || "-", col2, y + 4.5);

  const col3 = MARGIN + colW * 2;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.subtle);
  doc.text("FECHA DE CONSULTA", col3, y);
  doc.setFontSize(10.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.ink);
  doc.text(dateLabel, col3, y + 4.5);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.muted);
  doc.text(`Impreso el ${nowLabel}`, col3, y + 9);

  // Alto dinámico: la columna PACIENTE puede tener hasta 4 sublíneas (email, telefono, domicilio, nacimiento).
  y = Math.max(subY, y + 13) + 5;

  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y = gap(y, 5);

  // ── 3. DATOS CLÍNICOS DE LA CONSULTA ──────────────────────────────────────

  const hasConsultData = chiefComplaint || sessionNotes || treatmentsIndicated || treatmentsDone;

  if (hasConsultData) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.brand);
    doc.text("DATOS DE LA CONSULTA", MARGIN, y);
    y = gap(y, 5);

    const halfW = (CONTENT_W - 6) / 2;
    let yLeft = y;
    if (chiefComplaint) yLeft = fieldBlock(doc, "Motivo de consulta", chiefComplaint, MARGIN, yLeft, halfW);
    if (treatmentsDone) yLeft = fieldBlock(doc, "Tratamientos realizados", treatmentsDone, MARGIN, yLeft, halfW);
    let yRight = y;
    if (treatmentsIndicated) yRight = fieldBlock(doc, "Tratamientos indicados", treatmentsIndicated, MARGIN + halfW + 6, yRight, halfW);
    if (sessionNotes) {
      y = Math.max(yLeft, yRight) + 1;
      y = fieldBlock(doc, "Notas de la sesion", sessionNotes, MARGIN, y, CONTENT_W);
    } else {
      y = Math.max(yLeft, yRight);
    }

    doc.setDrawColor(...C.border);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y = gap(y, 5);
  }

  // ── 4. ANTECEDENTES MÉDICOS ────────────────────────────────────────────────

  const hasNewQuestionnaire = !!medicalQuestionnaire && hasQuestionnaireContent(medicalQuestionnaire);
  const hasLegacyAntecedents = !hasNewQuestionnaire && !!(medicalHistory || allergies || medications || generalNotes);

  function qaLine(label: string, answer: YesNo, detail: string | null, x: number, yPos: number, maxW: number): number {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C.ink);
    doc.text(`${label}: ${yesNoLabel(answer)}`, x, yPos);
    let ny = yPos + 4.5;
    if (detail && detail.trim()) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(...C.muted);
      const detailLines = doc.splitTextToSize(detail.trim(), maxW - 4) as string[];
      doc.text(detailLines, x + 4, ny);
      ny += detailLines.length * 4;
    }
    return ny + 1.5;
  }

  if (hasNewQuestionnaire && medicalQuestionnaire) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.brand);
    doc.text("ANTECEDENTES MEDICOS", MARGIN, y);
    y = gap(y, 5);

    y = qaLine("¿Está bajo cuidado médico?", medicalQuestionnaire.underMedicalCare, null, MARGIN, y, CONTENT_W);
    y = qaLine(
      "¿Está recibiendo medicación en la actualidad?",
      medicalQuestionnaire.currentMedication.answer,
      medicalQuestionnaire.currentMedication.detail,
      MARGIN, y, CONTENT_W,
    );
    y = gap(y, 2);

    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      head: [["Antecedente", "Respuesta", "Podría ampliar"]],
      body: medicalQuestionnaire.items.map((it) => [it.label, yesNoLabel(it.answer), it.detail || ""]),
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 22, halign: "center" },
        2: { cellWidth: "auto", fontStyle: "italic", textColor: C.muted },
      },
      headStyles: {
        fillColor: C.ink,
        textColor: C.white,
        fontStyle: "bold",
        fontSize: 7.5,
        cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: { top: 1.6, bottom: 1.6, left: 3, right: 3 },
        textColor: C.ink,
        valign: "top",
        lineColor: C.border,
        lineWidth: 0.2,
      },
      alternateRowStyles: { fillColor: C.rowAlt },
      pageBreak: "auto",
      showHead: "everyPage",
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    y = (doc as any).lastAutoTable?.finalY ?? y + 10;
    y = gap(y, 4);

    y = qaLine(
      "¿Recuerda cuándo recibió la vacuna antitetánica?",
      medicalQuestionnaire.tetanusVaccine.answer,
      medicalQuestionnaire.tetanusVaccine.date,
      MARGIN, y, CONTENT_W,
    );
    y = qaLine("¿Está embarazada o presume estarlo?", medicalQuestionnaire.pregnant, null, MARGIN, y, CONTENT_W);
    y = qaLine("¿Está amamantando?", medicalQuestionnaire.breastfeeding, null, MARGIN, y, CONTENT_W);

    if (medicalQuestionnaire.otherConditions.trim()) {
      y = fieldBlock(doc, "Otras enfermedades no mencionadas", medicalQuestionnaire.otherConditions.trim(), MARGIN, y, CONTENT_W);
    }

    doc.setDrawColor(...C.border);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y = gap(y, 5);
  } else if (hasLegacyAntecedents) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.brand);
    doc.text("ANTECEDENTES MEDICOS (formato anterior)", MARGIN, y);
    y = gap(y, 5);

    const halfW = (CONTENT_W - 6) / 2;
    let yLeft = y;
    let yRight = y;
    if (medicalHistory) yLeft = fieldBlock(doc, "Historia medica", medicalHistory, MARGIN, yLeft, halfW);
    if (allergies) yLeft = fieldBlock(doc, "Alergias conocidas", allergies, MARGIN, yLeft, halfW);
    if (medications) yRight = fieldBlock(doc, "Medicacion actual", medications, MARGIN + halfW + 6, yRight, halfW);
    if (generalNotes) yRight = fieldBlock(doc, "Notas generales", generalNotes, MARGIN + halfW + 6, yRight, halfW);

    y = Math.max(yLeft, yRight);
    doc.setDrawColor(...C.border);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y = gap(y, 5);
  }

  // ── 5. TABLA DE PIEZAS DENTALES ────────────────────────────────────────────

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.brand);
  doc.text("DETALLE DE PIEZAS DENTALES", MARGIN, y);
  y = gap(y, 4);

  const isSimpleV1 =
    !!odontogramJson &&
    typeof odontogramJson === "object" &&
    (odontogramJson as { version?: string }).version === "simple-v1";

  if (!isSimpleV1) {
    // Legacy ZoliQua format — show informational notice
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...C.muted);
    const noticeLines = doc.splitTextToSize(
      "Esta consulta fue registrada con el odontograma anterior y no puede mostrarse en el formato actual. " +
      "El estado del odontograma no se incluye en este PDF.",
      CONTENT_W,
    ) as string[];
    doc.text(noticeLines, MARGIN, y + 4);
    y = gap(y, noticeLines.length * 4.5 + 10);
  } else {
    const toothRows = parseSimpleV1(odontogramJson);

    if (toothRows.length === 0) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(...C.muted);
      doc.text("No se registraron condiciones en ninguna pieza para esta consulta.", MARGIN, y + 4);
      y = gap(y, 12);
    } else {
      autoTable(doc, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN },
        head: [["Pieza", "Nombre (FDI)", "Condicion / Superficies", "Nota"]],
        body: toothRows.map((r) => [
          `${r.toothNo}`,
          r.name,
          r.condition,
          r.note,
        ]),
        columnStyles: {
          0: { cellWidth: 14, halign: "center", fontStyle: "bold" },
          1: { cellWidth: 52 },
          2: { cellWidth: "auto" },
          3: { cellWidth: 36, fontStyle: "italic", textColor: C.muted },
        },
        headStyles: {
          fillColor: C.ink,
          textColor: C.white,
          fontStyle: "bold",
          fontSize: 7.5,
          cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
          textColor: C.ink,
          valign: "top",
          lineColor: C.border,
          lineWidth: 0.2,
        },
        alternateRowStyles: { fillColor: C.rowAlt },
        pageBreak: "auto",
        showHead: "everyPage",
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      y = (doc as any).lastAutoTable?.finalY ?? y + 10;
      y = gap(y, 5);
    }
  }

  // ── 6. AUTORIZACIÓN (texto estático, no editable desde la UI) ─────────────

  const AUTH_TEXT =
    "Autorizo al profesional actuante a realizar los tratamientos odontológicos que considere necesarios " +
    "en base al diagnóstico y a la información que declaro en esta ficha. Ratifico mi respuesta, con carácter de declaración jurada.";
  const authLines = doc.splitTextToSize(AUTH_TEXT, CONTENT_W) as string[];
  const AUTH_BLOCK_H = authLines.length * 4 + 8;

  if (y > PAGE_H - FOOTER_H - AUTH_BLOCK_H - 10) {
    doc.addPage();
    y = MARGIN;
  } else {
    y = gap(y, 4);
  }

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.ink);
  doc.text(authLines, MARGIN, y);
  y = gap(y, authLines.length * 4 + 4);

  // ── 7. ESPACIO PARA FIRMA ──────────────────────────────────────────────────
  // Bloque estático para firma en papel, igual que los formularios del consultorio.

  const SIGN_BLOCK_H = 38; // mm total del bloque
  const needsNewPage = y > PAGE_H - FOOTER_H - SIGN_BLOCK_H - 10;
  if (needsNewPage) {
    doc.addPage();
    y = MARGIN;
  } else {
    y = gap(y, 6);
  }

  // Dos columnas de firma
  const signColW = (CONTENT_W - 10) / 2;
  const signCols = [
    { x: MARGIN, label: "Firma y sello del profesional" },
    { x: MARGIN + signColW + 10, label: "Consentimiento del paciente" },
  ];

  for (const col of signCols) {
    // Línea de firma
    doc.setDrawColor(...C.ink);
    doc.setLineWidth(0.4);
    doc.line(col.x, y + 22, col.x + signColW, y + 22);

    // Etiqueta debajo de la línea
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C.muted);
    doc.text(col.label, col.x + signColW / 2, y + 27, { align: "center" });

    // Aclaración (sub-etiqueta)
    doc.setFontSize(7);
    doc.setTextColor(...C.subtle);
    doc.text("Nombre y aclaracion", col.x + signColW / 2, y + 31, { align: "center" });
  }

  y = gap(y, SIGN_BLOCK_H);

  // ── 8. DISCLAIMER ──────────────────────────────────────────────────────────

  if (y > PAGE_H - FOOTER_H - 20) {
    doc.addPage();
    y = MARGIN;
  }

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...C.subtle);
  const disclaimer =
    "Este documento es un registro interno de uso clinico y no constituye una historia clinica oficial " +
    "ni un documento con validez legal. La informacion debe ser verificada y resguardada por el profesional responsable.";
  const disclaimerLines = doc.splitTextToSize(disclaimer, CONTENT_W) as string[];
  doc.text(disclaimerLines, MARGIN, y + 2);

  // ── 9. FOOTER ──────────────────────────────────────────────────────────────

  drawFooter(doc);

  // ── 10. DESCARGA ────────────────────────────────────────────────────────────

  const safeClientName = clientName.replace(/[^a-z0-9áéíóúñ\s]/gi, "").trim().replace(/\s+/g, "-");
  const safeDate = date.slice(0, 10);
  doc.save(`historia-clinica-${safeClientName}-${safeDate}.pdf`);
}
